import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ExitIcon, InfoIcon } from '@/assets/icons';
import { MarveriIcon } from '@/pages/overview/common/MarveriIcon';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import { useOverview } from '@/pages/overview/common/utils';
import { columnInformation, CreateColumnFn } from '@/pages/overview/contract-clauses/common/utils';
import { ClauseIdentificationStep } from '@/pages/overview/contract-clauses/custom-column-wizard/ClauseIdentificationStep';
import { CustomColumnWizardSelect } from '@/pages/overview/contract-clauses/custom-column-wizard/CustomColumnWizardSelect';
import { InformationExtractionStep } from '@/pages/overview/contract-clauses/custom-column-wizard/InformationExtractionStep';
import { trpcReact } from '@/utils/trpc';

interface CustomColumnWizard {
  isWizardOpen: boolean;
  closeWizard: (val: boolean) => void;
  createContractClauseColumn: CreateColumnFn;
  contractClauseListRef: React.RefObject<HTMLDivElement>;
  columnHeaderList: string[];
  setColumnVisibility: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}
export const CustomColumnWizard = ({
  isWizardOpen,
  closeWizard,
  createContractClauseColumn,
  contractClauseListRef,
  columnHeaderList,
  setColumnVisibility,
}: CustomColumnWizard) => {
  const { matter } = useOverview();

  const defaultColumnList = useMemo(
    () => [
      { headerName: 'File Name', headerKey: 'file name', selected: true },
      { headerName: 'Description', headerKey: 'description', selected: true },
      { headerName: 'Document Name', headerKey: 'agreement_name', selected: false },
      { headerName: 'Parties', headerKey: 'parties', selected: false },
      { headerName: 'Document Date', headerKey: 'agreement_date', selected: false },
      { headerName: 'Effective Date', headerKey: 'effective_date', selected: false },
      { headerName: 'Expiration Date', headerKey: 'expiration_date', selected: false },
      { headerName: 'Renewal Period', headerKey: 'renewal_term', selected: false },
      {
        headerName: 'Notice to Terminate Renewal',
        headerKey: 'notice_to_terminate_renewal',
        selected: false,
      },
      {
        headerName: 'Termination for Convenience',
        headerKey: 'termination_for_convenience',
        selected: false,
      },
      { headerName: 'Anti-Assignment', headerKey: 'anti_assignment', selected: false },
      { headerName: 'Change of Control', headerKey: 'change_of_control', selected: false },
      { headerName: 'Non-Compete', headerKey: 'non_compete', selected: false },
      { headerName: 'Exclusivity', headerKey: 'exclusivity', selected: false },
      {
        headerName: 'Non-Solicit of Customers',
        headerKey: 'no_solicit_of_customers',
        selected: false,
      },
      { headerName: 'ROFO/ ROFR/ ROFN', headerKey: 'rofo_rofr_rofn', selected: false },
      { headerName: 'Most Favored Nation', headerKey: 'most_favored_nation', selected: false },
      { headerName: 'Governing Law', headerKey: 'governing_law', selected: false },
    ],
    [],
  );
  const [customColumnStep, setCustomColumnStep] = useState('select');
  const [selectedColumns, setSelectedColumns] = useState(defaultColumnList);
  const [selectedColumnCount, setSelectedColumnCount] = useState(
    matter.requestedDocumentAnalysisColumns?.length || 0,
  );
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    setSelectedColumns((columns) => {
      return columns.map((column) => {
        if (
          matter.requestedDocumentAnalysisColumns &&
          Array.isArray(matter.requestedDocumentAnalysisColumns) &&
          (matter.requestedDocumentAnalysisColumns as string[]).includes(column.headerKey)
        ) {
          return { ...column, selected: true };
        }
        return column;
      });
    });
    setSelectedColumnCount(matter.requestedDocumentAnalysisColumns?.length || 0);
  }, [matter.requestedDocumentAnalysisColumns]);

  const handleCloseWizard = () => {
    closeWizard(true);
    setCustomColumnStep('select');
  };

  const saveCustomColumn = trpcReact.dataRoom.saveCustomContractClause.useMutation();
  const updateColumnSelection =
    trpcReact.dataRoom.updateRequestedDocumentAnalysisColumns.useMutation();

  const createCustomColumn = (
    customType: string,
    customName: string,
    customLabel: string,
    customDescription: string,
    customElements: string,
    otherText: string,
    wideCol: boolean,
    canEdit: boolean,
  ) => {
    saveCustomColumn.mutate(
      {
        clientMatterId: matter.id,
        type: customType,
        name: customName,
        label: customLabel,
        elements: customElements,
        other: otherText,
        description: customDescription,
      },
      {
        onSuccess: () => {
          createContractClauseColumn(customType, wideCol, true, canEdit);
          updateColumnSelection.mutate({
            clientMatterId: matter.id,
            columns: [...matter.requestedDocumentAnalysisColumns, customName],
          });
          handleCloseWizard();
          if (contractClauseListRef.current) {
            contractClauseListRef.current.scrollTo({
              left: contractClauseListRef.current.scrollWidth,
              behavior: 'smooth',
            });
          }
        },
        onError: () => {
          alert(`Could not find data`);
        },
      },
    );
  };

  const getPreviouslySelectedColumns = (columnHeaderKey: string) => {
    if (
      matter.requestedDocumentAnalysisColumns?.includes(columnHeaderKey) ||
      columnHeaderKey === 'file name'
    ) {
      return true;
    }
    return false;
  };

  const handleColumnSelection = (columnHeaderKey: string) => {
    if (getPreviouslySelectedColumns(columnHeaderKey)) return;
    else {
      setSelectedColumns(
        selectedColumns.map((col) => {
          if (col.headerKey === columnHeaderKey) {
            const newSelected = !col.selected;
            setSelectedColumnCount((prev) => prev + (newSelected ? 1 : -1));
            return { ...col, selected: newSelected };
          }
          return col;
        }),
      );
    }
  };

  const saveAndRunColumnSelection = () => {
    if (selectedColumnCount <= matter.requestedDocumentAnalysisColumns?.length) return;
    const columnsToSave = selectedColumns.filter(
      (col) => col.selected && !matter.requestedDocumentAnalysisColumns?.includes(col.headerKey),
    );
    updateColumnSelection.mutate(
      {
        clientMatterId: matter.id,
        columns: columnsToSave.map((col) => col.headerKey),
      },
      {
        onSuccess: () => {
          setColumnVisibility((prev) => {
            const newVisibility = { ...prev };
            columnsToSave.forEach((col) => {
              newVisibility[col.headerName] = true;
            });
            return newVisibility;
          });
          handleCloseWizard();
        },
        onError: () => {
          alert(`Could not add columns`);
        },
      },
    );
  };

  const isCustomColumnButtonDisabled = useCallback(() => {
    if (selectedColumnCount <= matter.requestedDocumentAnalysisColumns?.length) return false;
    return true;
  }, [selectedColumnCount, matter.requestedDocumentAnalysisColumns]);

  const getColumnInformation = (columnHeader: string) => {
    return columnInformation[columnHeader];
  };

  return (
    <Dialog
      open={isWizardOpen}
      onClose={handleCloseWizard}
      className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto bg-black/50"
      data-testid="custom-column-wizard"
    >
      <DialogPanel className="flex flex-col gap-8 rounded-[16px] bg-[#242324] p-8">
        <div className="flex justify-between">
          <DialogTitle className="text-[24px] font-medium text-[#EAE6EA]">
            {customColumnStep !== 'select' ? 'Create Columns' : 'Add Columns'}
          </DialogTitle>

          <MarveriIcon icon={ExitIcon} onClick={handleCloseWizard} iconStyle="" iconType="other" />
        </div>

        {customColumnStep === 'select' && (
          <div>
            <h3 className="mb-6 text-[14px] font-medium text-[#EAE6EA]">
              Select which columns to process below.
            </h3>
            <div className="relative rounded-[8px] bg-[#171617] py-3 pr-3">
              <ScrollableDiv
                containerStyle="bg-[#171617] text-[#CECACE] font-medium text-[12px] max-h-[306px]"
                scrollKey="custom-column-wizard"
              >
                {selectedColumns.map((column, index) => {
                  return (
                    <div key={column.headerKey} onMouseLeave={() => setHoverIndex(null)}>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={column.selected}
                          onChange={() => handleColumnSelection(column.headerKey)}
                          className={`${getPreviouslySelectedColumns(column.headerKey) ? 'cursor-not-allowed checked:bg-[url("@/assets/icons/cc-checkmark-gray.svg")]' : 'cursor-pointer checked:bg-[url("@/assets/icons/cc-checkmark-light.svg")]'} size-[20px] appearance-none rounded-[3px] border border-N300 bg-contain  bg-center bg-no-repeat checked:border-transparent`}
                          data-testid="add-column-checkbox"
                        />
                        <span>{column.headerName}</span>
                        <div>
                          <img
                            src={InfoIcon}
                            className="size-4 hover:filter-hover-filter"
                            onMouseEnter={() => setHoverIndex(index)}
                          />
                        </div>
                      </div>
                      {hoverIndex === index && (
                        <div
                          className="absolute bottom-4 right-8 z-10 max-w-[250px] rounded-[4px] bg-[#242324] p-4 text-[12px] transition-width duration-300 ease-in-out"
                          onMouseLeave={() => setHoverIndex(null)}
                        >
                          {getColumnInformation(column.headerName)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </ScrollableDiv>
            </div>
            <div className="flex items-center gap-4 py-8">
              <div className="h-px w-full bg-[#353336]"></div>
              <span className="text-[14px] font-medium text-N400">OR</span>
              <div className="h-px w-full bg-[#353336]"></div>
            </div>
            <h3 className="text-[14px] font-medium text-[#EAE6EA]">Create a custom column.</h3>
          </div>
        )}
        {customColumnStep === 'select' && (
          <CustomColumnWizardSelect
            setCustomColumnStep={setCustomColumnStep}
            isDisabled={isCustomColumnButtonDisabled()}
          />
        )}
        {customColumnStep === 'identification' && (
          <ClauseIdentificationStep
            setCustomColumnStep={setCustomColumnStep}
            createCustomColumn={createCustomColumn}
            isSubmitting={saveCustomColumn.isLoading}
            columnHeaderList={columnHeaderList}
          />
        )}
        {customColumnStep === 'extraction' && (
          <InformationExtractionStep
            setCustomColumnStep={setCustomColumnStep}
            createCustomColumn={createCustomColumn}
            isSubmitting={saveCustomColumn.isLoading}
            columnHeaderList={columnHeaderList}
          />
        )}
        {customColumnStep === 'select' && (
          <div className="flex items-center justify-end gap-4">
            <div
              className="cursor-pointer text-N200 hover:text-[#EAE6EA]"
              onClick={handleCloseWizard}
            >
              Cancel
            </div>

            <div
              className={`${selectedColumnCount <= matter.requestedDocumentAnalysisColumns?.length ? 'cursor-default bg-[#353336] text-N400' : 'cursor-pointer bg-[#EAE6EA] hover:bg-N200'} rounded-full px-[18px] py-3 text-[14px] font-medium `}
              onClick={saveAndRunColumnSelection}
            >
              Run
            </div>
          </div>
        )}
      </DialogPanel>
    </Dialog>
  );
};
